/**
 * 🧠 PSYCHE QUIZ - Questions douces
 * 
 * "Quelques scénarios de vie pour mieux te connaître..."
 * 
 * ⚠️ RÈGLE D'OR : L'utilisateur ne voit JAMAIS les mots "complexe", "blessure", "trauma"
 *    Les questions sont des scénarios de vie normaux et légers.
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../../../ui/button';
import { ChevronRight, ChevronLeft, Brain, Sparkles, Check } from 'lucide-react';
import axios from 'axios';
import confetti from 'canvas-confetti';
import { PixieInline } from '../../PixieAvatar';

// ============================================
// QUESTIONS (chargées depuis l'API)
// ============================================

const DEFAULT_QUESTIONS = [
  {
    id: 'pq_1',
    scenario: "Ton/ta partenaire part en voyage une semaine sans toi...",
    question: "Comment tu te sens ?",
    options: [
      { id: 'a', text: "Cool, ça va me faire du bien aussi" },
      { id: 'b', text: "Un peu inquiet(e), j'espère qu'il/elle pensera à moi" },
      { id: 'c', text: "Je profite pour faire mes trucs" },
      { id: 'd', text: "Stressé(e), et s'il/elle rencontrait quelqu'un ?" }
    ]
  },
  {
    id: 'pq_2',
    scenario: "Tu postes une photo et tu reçois peu de likes...",
    question: "Ta première réaction ?",
    options: [
      { id: 'a', text: "Osef, je l'aime bien moi" },
      { id: 'b', text: "Je la supprime, trop gênant" },
      { id: 'c', text: "Je me demande ce qui cloche" },
      { id: 'd', text: "Je me dis que les gens sont occupés" }
    ]
  }
];

// ============================================
// COMPOSANT PRINCIPAL
// ============================================

export const PsycheQuiz = ({ 
  customerId, 
  onComplete, 
  onSkip,
  isDarkMode = false 
}) => {
  const [step, setStep] = useState(0); // 0: intro, 1-10: questions, 11: complete
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Charger les questions depuis l'API
  useEffect(() => {
    const loadQuestions = async () => {
      try {
        const API_URL = process.env.REACT_APP_BACKEND_URL;
        const response = await axios.get(`${API_URL}/api/psyche/quiz/questions`);
        setQuestions(response.data.questions || DEFAULT_QUESTIONS);
      } catch (error) {
        console.error('Erreur chargement questions:', error);
        setQuestions(DEFAULT_QUESTIONS);
      } finally {
        setIsLoading(false);
      }
    };
    loadQuestions();
  }, []);

  const totalQuestions = questions.length;
  const currentQuestion = step > 0 && step <= totalQuestions ? questions[step - 1] : null;
  const progress = step > 0 ? Math.min((step / totalQuestions) * 100, 100) : 0;

  const handleSelectAnswer = (optionId) => {
    if (currentQuestion) {
      setAnswers(prev => ({
        ...prev,
        [currentQuestion.id]: optionId
      }));
    }
  };

  const handleNext = async () => {
    if (step === totalQuestions) {
      // Soumettre les réponses
      setIsSubmitting(true);
      try {
        const API_URL = process.env.REACT_APP_BACKEND_URL;
        await axios.post(`${API_URL}/api/psyche/quiz/submit/${customerId}`, {
          answers
        });
        
        // Confetti subtil
        confetti({
          particleCount: 50,
          spread: 60,
          origin: { y: 0.7 },
          colors: ['#8B5CF6', '#EC4899', '#6366F1']
        });
        
        setStep(totalQuestions + 1);
      } catch (error) {
        console.error('Erreur soumission quiz:', error);
      } finally {
        setIsSubmitting(false);
      }
    } else {
      setStep(step + 1);
    }
  };

  const handleBack = () => {
    if (step > 0) setStep(step - 1);
  };

  const canProceed = step === 0 || step > totalQuestions || 
    (currentQuestion && answers[currentQuestion.id]);

  if (isLoading) {
    return (
      <div className="min-h-[400px] flex items-center justify-center">
        <div className="animate-pulse text-center">
          <span className="text-4xl block mb-2">🧠</span>
          <span className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>Chargement...</span>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-[450px] flex flex-col ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
      {/* Progress bar */}
      {step > 0 && step <= totalQuestions && (
        <div className="mb-4">
          <div className={`h-1 rounded-full ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
            <motion.div 
              className="h-full rounded-full bg-gradient-to-r from-purple-500 to-pink-500"
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
            />
          </div>
          <div className={`text-xs mt-1 text-right ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
            {step} / {totalQuestions}
          </div>
        </div>
      )}

      <AnimatePresence mode="wait">
        {/* Intro */}
        {step === 0 && (
          <motion.div
            key="intro"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="flex-1 flex flex-col items-center justify-center text-center px-4"
          >
            <span className="text-6xl mb-4">🌟</span>
            <h2 className="text-2xl font-bold mb-2">Petits scénarios de vie</h2>
            <p className={`mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              Quelques situations du quotidien pour mieux te connaître...
            </p>
            <p className={`text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              Réponds instinctivement, il n'y a pas de mauvaise réponse ! 💜
            </p>
            <p className={`text-xs mt-4 ${isDarkMode ? 'text-gray-600' : 'text-gray-300'}`}>
              ⏱️ ~3 minutes
            </p>
          </motion.div>
        )}

        {/* Questions */}
        {step > 0 && step <= totalQuestions && currentQuestion && (
          <motion.div
            key={currentQuestion.id}
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
            className="flex-1 flex flex-col"
          >
            {/* Scenario */}
            <div className={`p-4 rounded-xl mb-4 ${isDarkMode ? 'bg-gray-800' : 'bg-purple-50'}`}>
              <p className={`text-sm italic ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                "{currentQuestion.scenario}"
              </p>
            </div>

            {/* Question */}
            <h3 className="text-lg font-semibold mb-4 text-center">
              {currentQuestion.question}
            </h3>

            {/* Options */}
            <div className="flex-1 space-y-2">
              {currentQuestion.options.map((option, index) => {
                const isSelected = answers[currentQuestion.id] === option.id;
                const letters = ['A', 'B', 'C', 'D'];
                
                return (
                  <motion.button
                    key={option.id}
                    whileHover={{ scale: 1.01 }}
                    whileTap={{ scale: 0.99 }}
                    onClick={() => handleSelectAnswer(option.id)}
                    className={`w-full p-3 rounded-xl text-left transition-all flex items-start gap-3 ${
                      isSelected
                        ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg'
                        : isDarkMode 
                          ? 'bg-gray-800 hover:bg-gray-700 border border-gray-700' 
                          : 'bg-white hover:bg-gray-50 border border-gray-200'
                    }`}
                  >
                    <span className={`w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 ${
                      isSelected 
                        ? 'bg-white/20' 
                        : isDarkMode ? 'bg-gray-700' : 'bg-gray-100'
                    }`}>
                      {isSelected ? <Check className="w-4 h-4" /> : letters[index]}
                    </span>
                    <span className="flex-1 text-sm">{option.text}</span>
                  </motion.button>
                );
              })}
            </div>
          </motion.div>
        )}

        {/* Complete */}
        {step > totalQuestions && (
          <motion.div
            key="complete"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="flex-1 flex flex-col items-center justify-center text-center px-4"
          >
            <span className="text-6xl mb-4">✨</span>
            <h2 className="text-2xl font-bold mb-2">Merci !</h2>
            <p className={`mb-6 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              Tes réponses vont nous aider à te trouver la personne idéale...
            </p>
            <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-gray-800' : 'bg-purple-50'}`}>
              <p className={`text-sm flex items-center gap-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                <PixieInline size="md" /> Pixie analyse tout ça pour toi !
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Navigation buttons */}
      <div className="flex gap-3 mt-4">
        {step > 0 && step <= totalQuestions && (
          <Button
            variant="outline"
            onClick={handleBack}
            className={isDarkMode ? 'border-gray-600' : ''}
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Retour
          </Button>
        )}
        
        <Button
          onClick={step > totalQuestions ? onComplete : handleNext}
          disabled={!canProceed || isSubmitting}
          className="flex-1 bg-gradient-to-r from-purple-500 to-pink-500 text-white"
        >
          {isSubmitting ? (
            <span className="animate-pulse">Analyse en cours...</span>
          ) : step === 0 ? (
            <>C'est parti ! <Sparkles className="w-4 h-4 ml-1" /></>
          ) : step > totalQuestions ? (
            <>Continuer <ChevronRight className="w-4 h-4 ml-1" /></>
          ) : step === totalQuestions && canProceed ? (
            <>Terminer <Check className="w-4 h-4 ml-1" /></>
          ) : (
            <>Suivant <ChevronRight className="w-4 h-4 ml-1" /></>
          )}
        </Button>
      </div>

      {/* Skip button */}
      {step <= totalQuestions && onSkip && (
        <button
          onClick={onSkip}
          className={`mt-3 text-sm ${isDarkMode ? 'text-gray-500 hover:text-gray-400' : 'text-gray-400 hover:text-gray-500'}`}
        >
          Passer pour l'instant
        </button>
      )}
    </div>
  );
};

export default PsycheQuiz;
